import React, { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/features/shadcn/ui/dialog'
import { Button } from '@/features/shadcn/ui/button'
import { Switch } from '@/features/shadcn/ui/switch'
import { Badge } from '@/features/shadcn/ui/badge'
import { cn } from '@/features/utils'

export interface SubaccountSetupEntry {
  account_id: string
  account_name: string | null
  ai_enabled: boolean
}

export const PLATFORM_LABELS: Record<string, string> = {
  google_analytics: 'Google Analytics',
  google_tag_manager: 'Google Tag Manager',
  google_ads: 'Google Ads',
  google_search_console: 'Google Search Console',
  bigquery: 'Google BigQuery',
  google_merchant_center: 'Google Merchant Center',
}

/** Short labels for compact step dots in multi-product Marketing setup. */
export const PLATFORM_SHORT_LABELS: Record<string, string> = {
  google_analytics: 'Analytics',
  google_tag_manager: 'Tag Manager',
  google_ads: 'Ads',
  google_search_console: 'Search Console',
  bigquery: 'BigQuery',
  google_merchant_center: 'Merchant Center',
}

function normalizedAccountName(sub: SubaccountSetupEntry): string {
  return typeof sub.account_name === 'string' ? sub.account_name.trim() : ''
}

/** Last 4 digits of the platform leaf id (after the final `:`); alnum fallback when no digits. */
export function subaccountIdSuffix(accountId: string): string {
  const leaf = String(accountId || '').split(':').pop() || String(accountId || '')
  const digits = leaf.replace(/\D/g, '')
  if (digits.length >= 4) return digits.slice(-4)
  if (digits.length > 0) return digits
  const alnum = leaf.replace(/[^a-zA-Z0-9]/g, '')
  return alnum.slice(-4)
}

/**
 * Human-readable label for a sub-account row — never exposes raw connection/hash IDs.
 * When multiple rows share the same name, appends the last 4 id digits for disambiguation.
 */
export function subaccountDisplayName(
  sub: SubaccountSetupEntry,
  index = 0,
  siblings: SubaccountSetupEntry[] = []
): string {
  const name = normalizedAccountName(sub)
  const base = name || `Account ${index + 1}`
  if (!name) return base

  const key = name.toLowerCase()
  const hasDuplicate = siblings.some(
    (other, i) => i !== index && normalizedAccountName(other).toLowerCase() === key
  )
  if (!hasDuplicate) return base

  const suffix = subaccountIdSuffix(sub.account_id)
  return suffix ? `${base} (${suffix})` : base
}

function SetupStepIndicator({
  platforms,
  currentIndex,
}: {
  platforms: string[]
  currentIndex: number
}) {
  if (platforms.length <= 1) return null

  return (
    <div className="tw-mb-1 tw-space-y-3">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
        <Badge
          variant="secondary"
          className="tw-border-transparent tw-bg-blue-50 tw-text-blue-700 hover:tw-bg-blue-50"
        >
          Step {currentIndex + 1} of {platforms.length}
        </Badge>
        <p className="tw-text-xs tw-text-slate-500 tw-m-0 tw-text-right">
          You&apos;ll set up each connected Google product in turn
        </p>
      </div>
      <div className="tw-flex tw-items-start tw-gap-1.5" role="list" aria-label="Setup progress">
        {platforms.map((platform, i) => {
          const done = i < currentIndex
          const active = i === currentIndex
          const label = PLATFORM_SHORT_LABELS[platform] || PLATFORM_LABELS[platform] || platform
          return (
            <React.Fragment key={`${platform}-${i}`}>
              {i > 0 && (
                <div
                  className={cn(
                    'tw-mt-3.5 tw-h-0.5 tw-min-w-[8px] tw-flex-1 tw-rounded-full tw-transition-colors',
                    done || active ? 'tw-bg-blue-500' : 'tw-bg-slate-200'
                  )}
                  aria-hidden
                />
              )}
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-center tw-gap-1.5" role="listitem">
                <div
                  className={cn(
                    'tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-text-[11px] tw-font-semibold tw-transition-all',
                    done && 'tw-bg-blue-600 tw-text-white',
                    active && 'tw-bg-blue-600 tw-text-white tw-ring-4 tw-ring-blue-100',
                    !done && !active && 'tw-bg-slate-100 tw-text-slate-500'
                  )}
                  aria-current={active ? 'step' : undefined}
                >
                  {i + 1}
                </div>
                <span
                  className={cn(
                    'tw-max-w-full tw-truncate tw-text-center tw-text-[10px] tw-font-medium tw-leading-tight',
                    active ? 'tw-text-blue-700' : 'tw-text-slate-500'
                  )}
                  title={PLATFORM_LABELS[platform] || platform}
                >
                  {label}
                </span>
              </div>
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

interface Props {
  show: boolean
  loading?: boolean
  platform: string
  subaccounts: SubaccountSetupEntry[]
  /** X / Escape / outside click — dismiss the whole setup flow (do not advance Marketing queue). */
  onClose: () => void
  /**
   * Skip button. For multi-step Marketing flows this should advance to the next product;
   * defaults to `onClose` when omitted.
   */
  onSkip?: () => void
  onSave: (selections: SubaccountSetupEntry[]) => Promise<void>
  saving?: boolean
  /** Full ordered platform list for multi-product Marketing setup (e.g. GA → GTM → GSC → BQ). */
  stepPlatforms?: string[]
  /** 0-based index of the currently open platform within `stepPlatforms`. */
  stepIndex?: number
}

export default function AiSubaccountSetupModal({
  show,
  loading = false,
  platform,
  subaccounts,
  onClose,
  onSkip,
  onSave,
  saving = false,
  stepPlatforms,
  stepIndex = 0,
}: Props) {
  const [localSubs, setLocalSubs] = useState<SubaccountSetupEntry[]>([])

  useEffect(() => {
    if (!show) return
    setLocalSubs(subaccounts.map((s) => ({ ...s, ai_enabled: true })))
  }, [show, subaccounts])

  const platformLabel = PLATFORM_LABELS[platform] || platform
  const multiStep = (stepPlatforms?.length || 0) > 1
  const currentStepIndex = Math.min(Math.max(stepIndex, 0), (stepPlatforms?.length || 1) - 1)
  const handleSkip = onSkip || onClose

  const toggleSub = (accountId: string) => {
    setLocalSubs((prev) =>
      prev.map((s) => (s.account_id === accountId ? { ...s, ai_enabled: !s.ai_enabled } : s))
    )
  }

  const enableAll = () => {
    setLocalSubs((prev) => prev.map((s) => ({ ...s, ai_enabled: true })))
  }

  const disableAll = () => {
    setLocalSubs((prev) => prev.map((s) => ({ ...s, ai_enabled: false })))
  }

  const handleSave = async () => {
    await onSave(localSubs)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open && !saving) onClose()
  }

  return (
    <Dialog open={show} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'sm:tw-max-w-xl tw-flex tw-max-h-[min(92dvh,900px)] tw-flex-col tw-gap-0 tw-overflow-hidden tw-p-0',
          'max-sm:tw-top-[max(1rem,env(safe-area-inset-top,0px))] max-sm:tw-translate-y-0'
        )}
        hideClose={saving}
        onInteractOutside={(e) => {
          if (saving) e.preventDefault()
        }}
        onEscapeKeyDown={(e) => {
          if (saving) e.preventDefault()
        }}
      >
        <div className="tw-shrink-0 tw-border-b tw-border-slate-100 tw-bg-gradient-to-b tw-from-slate-50 tw-to-white tw-px-6 tw-pt-6 tw-pb-5">
          <DialogHeader className="tw-space-y-3 tw-text-left tw-pr-8">
            {multiStep && stepPlatforms ? (
              <SetupStepIndicator platforms={stepPlatforms} currentIndex={currentStepIndex} />
            ) : null}
            <div className="tw-space-y-1.5">
              <DialogTitle className="tw-text-xl">Set up AI access</DialogTitle>
              <DialogDescription className="tw-text-sm tw-text-slate-600">
                <span className="tw-font-medium tw-text-slate-800">{platformLabel}</span>
                {' — '}
                choose which accounts the AI agent can use. You can change this later in Settings → My
                AI.
              </DialogDescription>
            </div>
          </DialogHeader>
        </div>

        <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-5">
          {loading ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-12 tw-text-slate-500">
              <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" aria-hidden />
              <span className="tw-text-sm">Loading accounts…</span>
            </div>
          ) : localSubs.length === 0 ? (
            <div className="tw-rounded-lg tw-border tw-border-dashed tw-border-slate-200 tw-bg-slate-50 tw-px-4 tw-py-8 tw-text-center">
              <p className="tw-text-sm tw-text-slate-500 tw-m-0">
                No sub-accounts were found for this connection. You can fetch them later from Settings
                → My AI.
              </p>
            </div>
          ) : (
            <div className="tw-space-y-3">
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <p className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500 tw-m-0">
                  {localSubs.length} account{localSubs.length === 1 ? '' : 's'}
                </p>
                <div className="tw-flex tw-gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={enableAll}
                    disabled={saving}
                    className="tw-h-8 tw-px-2 tw-text-xs tw-text-blue-600 hover:tw-text-blue-800"
                  >
                    Enable all
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={disableAll}
                    disabled={saving}
                    className="tw-h-8 tw-px-2 tw-text-xs tw-text-slate-500 hover:tw-text-slate-700"
                  >
                    Disable all
                  </Button>
                </div>
              </div>

              <ul className="tw-m-0 tw-list-none tw-space-y-2 tw-p-0">
                {localSubs.map((sub, index) => {
                  const label = subaccountDisplayName(sub, index, localSubs)
                  return (
                    <li
                      key={sub.account_id}
                      className={cn(
                        'tw-flex tw-items-center tw-justify-between tw-gap-4 tw-rounded-xl tw-border tw-px-4 tw-py-3 tw-transition-colors',
                        sub.ai_enabled
                          ? 'tw-border-blue-100 tw-bg-blue-50/40'
                          : 'tw-border-slate-200 tw-bg-white'
                      )}
                    >
                      <div className="tw-min-w-0 tw-flex-1">
                        <div className="tw-break-words tw-text-sm tw-font-medium tw-text-slate-900">
                          {label}
                        </div>
                      </div>
                      <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2.5">
                        <span
                          className={cn(
                            'tw-text-xs tw-font-medium',
                            sub.ai_enabled ? 'tw-text-blue-700' : 'tw-text-slate-400'
                          )}
                        >
                          {sub.ai_enabled ? 'On' : 'Off'}
                        </span>
                        <Switch
                          checked={sub.ai_enabled}
                          disabled={saving}
                          onCheckedChange={() => toggleSub(sub.account_id)}
                          className="data-[state=checked]:tw-bg-blue-600"
                          aria-label={`Toggle AI access for ${label}`}
                        />
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="tw-shrink-0 tw-gap-2 tw-border-t tw-border-slate-100 tw-bg-slate-50/80 tw-px-6 tw-py-4 sm:tw-space-x-2">
          <Button type="button" variant="outline" onClick={handleSkip} disabled={saving}>
            {multiStep && currentStepIndex < (stepPlatforms?.length || 0) - 1 ? 'Skip for now' : 'Skip'}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={loading || saving || localSubs.length === 0}
          >
            {saving ? (
              <>
                <Loader2 className="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin" aria-hidden />
                Saving…
              </>
            ) : multiStep && currentStepIndex < (stepPlatforms?.length || 0) - 1 ? (
              'Save & continue'
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
